import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, ShieldCheck, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Glass, SectionTitle } from "./ui";
import { DispositivosLista } from "@/components/DispositivosLista";
import { useMobile } from "./MobileApp";
import {
  activarHuella, estadoHuella, huellaDisponible, listarHuellas, quitarHuella, setBloqueoApertura,
} from "@/lib/passkeys";

export default function Seguridad() {
  const { me } = useMobile();
  const [disponible, setDisponible] = useState(null);
  const [estado, setEstado] = useState(null);
  const [huellas, setHuellas] = useState([]);
  const [busy, setBusy] = useState(false);

  const cargar = async () => {
    try {
      setEstado(await estadoHuella());
      setHuellas(await listarHuellas());
    } catch (_) { /* sin sesión */ }
  };
  useEffect(() => { huellaDisponible().then(setDisponible); cargar(); }, []);

  // El estado real manda: si el equipo tiene el bloqueo activo, se refleja en la app
  useEffect(() => {
    if (estado?.bloqueo_apertura) localStorage.setItem("aipos_bio_lock", "1");
    else if (estado) localStorage.removeItem("aipos_bio_lock");
  }, [estado?.bloqueo_apertura]); // eslint-disable-line react-hooks/exhaustive-deps

  const activar = async () => {
    setBusy(true);
    try {
      const r = await activarHuella();   // el nombre sale del navegador y el equipo
      toast.success(`Huella activada en ${r.nombre}`);
      await cargar();
    } catch (e) {
      if (e?.name === "NotAllowedError") toast.error("Cancelaste la verificación");
      else if (e?.name === "InvalidStateError") toast.info("Esta cuenta ya tiene la huella activada en este equipo");
      else toast.error(e?.response?.data?.detail || e?.message || "No se pudo activar la huella");
    } finally { setBusy(false); }
  };

  const quitar = async (id) => {
    if (!window.confirm("¿Quitar la huella de este equipo? Tendrás que ingresar con tu contraseña.")) return;
    try { await quitarHuella(id); toast.success("Huella quitada"); await cargar(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo quitar"); }
  };

  const toggleBloqueo = async (v) => {
    try {
      await setBloqueoApertura(v);
      if (v) localStorage.setItem("aipos_bio_lock", "1");
      else localStorage.removeItem("aipos_bio_lock");
      toast.success(v ? "Pediremos tu huella cada vez que abras la app" : "Bloqueo desactivado en este equipo");
      await cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cambiar"); }
  };

  const esJefe = ["DUENO", "ADMIN_EMPRESA"].includes(me?.user?.role_real || me?.user?.role);

  return (
    <div className="pt-5" data-testid="mobile-seguridad">
      <h1 className="text-2xl font-bold font-display">Seguridad</h1>
      <p className="text-xs text-slate-400 mt-1">Huella de este equipo y sesiones abiertas</p>

      <SectionTitle>Entrar con huella o Face ID</SectionTitle>
      <Glass className="p-4" data-testid="seguridad-huella">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl grid place-items-center bg-indigo-500/10 border border-indigo-500/30 text-indigo-300">
            <Fingerprint className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Huella en este equipo</p>
            <p className="text-xs text-slate-400 mt-0.5" data-testid="seguridad-huella-estado">
              {disponible === null ? "Revisando…"
                : !disponible ? "Este equipo no tiene huella ni Face ID configurados"
                : estado?.huellas ? `Activada · ${estado.huellas} equipo(s)`
                : "Sin activar: usa tu contraseña para entrar"}
            </p>
          </div>
        </div>
        {disponible && (
          <button data-testid="seguridad-activar-huella" disabled={busy} onClick={activar}
                  className="mt-3 w-full h-11 rounded-2xl bg-indigo-500 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
            <Fingerprint className="w-4 h-4" />
            {busy ? "Esperando tu huella…" : estado?.huellas ? "Activar en otro equipo" : "Activar en este equipo"}
          </button>
        )}
        {huellas.length > 0 && (
          <div className="mt-3 space-y-2">
            {huellas.map((h) => (
              <div key={h.credential_id} data-testid={`seguridad-huella-${h.credential_id.slice(0, 8)}`}
                   className="flex items-center justify-between gap-2 rounded-xl border border-white/10 px-3 py-2">
                <span className="text-xs text-slate-300 truncate">
                  {h.nombre}
                  {h.creado_at ? <span className="text-slate-500"> · {new Date(h.creado_at).toLocaleDateString("es-CL", { day: "2-digit", month: "short" })}</span> : null}
                </span>
                <button onClick={() => quitar(h.credential_id)} data-testid={`seguridad-quitar-huella-${h.credential_id.slice(0, 8)}`}
                        className="text-rose-400 shrink-0" aria-label="Quitar">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Glass>

      <Glass className="p-4 mt-3" data-testid="seguridad-bloqueo">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl grid place-items-center bg-cyan-500/10 border border-cyan-500/30 text-cyan-300">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Pedir huella al abrir la app</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Cada vez que abras Ai-POS en este equipo se pedirá tu huella, aunque la sesión siga activa.
            </p>
          </div>
          <Switch data-testid="seguridad-bloqueo-toggle" disabled={!disponible || !estado?.huellas}
                  checked={!!estado?.bloqueo_apertura} onCheckedChange={toggleBloqueo} />
        </div>
      </Glass>

      <SectionTitle>Mis dispositivos</SectionTitle>
      <div className="pb-4">
        <DispositivosLista oscuro puedeVerTodos={esJefe} />
      </div>
    </div>
  );
}
